import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  DefaultValueAccessor,
  FormControl,
  FormGroupDirective,
  NG_VALUE_ACCESSOR,
  NgControl,
  NgForm,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { debounceTime, Subject, takeUntil } from 'rxjs';

import { SelectOptionData } from 'src/app/shared/interface/profet-control-widget.interface';

@Component({
  selector: 'profet-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useClass: DefaultValueAccessor,
      multi: true,
    },
  ],
})
export class SelectComponent implements AfterViewInit, OnDestroy, OnChanges {
  /** Stores all or filtered options based on search */
  filteredOptions: Array<SelectOptionData> = [];

  searchCtrl: FormControl = new FormControl([]);

  // isDisabled: boolean = false;

  onDestroyNotifier$ = new Subject<void>();

  @Input() label: string | null = null;
  @Input() outerLabel: string | null = null;
  @Input() multiple: boolean = false;
  @Input() showEmpty: boolean = false;
  @Input() canSearch: boolean = true;
  @Input() required: boolean = false;
  @Input() inProgress: boolean = false;
  @Input() options: Array<SelectOptionData> = [];
  @Input() maxSelection: number | null = null;
  @Input() subscriptSizing: 'dynamic' | 'fixed' = 'dynamic';

  /** The dropdown's width: 'auto' matches the field, null grows to fit the longest option. */
  @Input() panelWidth: string | number | null = 'auto';

  @ViewChild(MatSelect) matSelect?: MatSelect;
  @ViewChild('searchInput', { read: ElementRef }) searchInput?: ElementRef;

  @HostBinding('class.in-progress') get inProgressClass() {
    return this.inProgress;
  }

  constructor(
    private _cdr: ChangeDetectorRef,
    public ngControl: NgControl
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['showEmpty']?.previousValue !== changes['showEmpty']?.currentValue ||
      changes['options']?.previousValue !== changes['options']?.currentValue
    ) {
      this._filterOptions();
    }

    if (changes['inProgress']?.previousValue != changes['inProgress']?.currentValue) {
      if (changes['inProgress']?.currentValue == true) {
        this.modelCtrl.disable();
      } else {
        this.modelCtrl.enable();
      }
    }
  }

  ngAfterViewInit(): void {
    // Disable the control if it is disabled in the parent form
    // this.isDisabled = this.ngControl.status === 'DISABLED';
    // this.ngControl.statusChanges
    //   ?.pipe(takeUntil(this.onDestroyNotifier$))
    //   .subscribe((status) => (this.isDisabled = status === 'DISABLED'));

    this.searchCtrl.valueChanges.pipe(takeUntil(this.onDestroyNotifier$), debounceTime(300)).subscribe((res) => {
      this._filterOptions(res);
      this._cdr.detectChanges();
    });

    this.matSelect?.openedChange.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((isOpen) => {
      if (!isOpen) this.searchCtrl.setValue(null);
      this._focusSearchInput();
    });
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier$.next();
    this.onDestroyNotifier$.complete();
  }

  compareWithFn(option: string, selection: string) {
    return option == selection;
  }

  shouldDisableOption(key: any): boolean {
    if (!this.multiple || this.maxSelection == null) return false;

    const selected = this.modelCtrlValue ?? [];

    // Disable if max selected and this key is not already selected
    return selected.length >= this.maxSelection && !selected.includes(key);
  }

  private _filterOptions(searchText?: string) {
    if (!searchText) {
      if (this.showEmpty) {
        if (!this.options?.length) this.filteredOptions = [{ key: null, displayValue: '(None)' }];
        else this.filteredOptions = [{ key: null, displayValue: '(None)' }, ...this.options];
      } else this.filteredOptions = this.options;
    } else {
      this.filteredOptions = this.options.filter((option) => {
        if (option.children) {
          return option.children.filter((type) => type.displayValue?.toLowerCase().includes(searchText.toLowerCase()))
            ?.length
            ? true
            : false;
        } else {
          return ((option.displayValue ?? '') + (option.subText ?? ''))
            ?.toLowerCase()
            .includes(searchText.toLowerCase());
        }
      });
    }
  }

  private _focusSearchInput() {
    if (!this.searchInput) return;

    this.searchInput.nativeElement.focus();
  }

  get modelCtrl(): FormControl {
    return this.ngControl.control as FormControl;
  }

  get modelCtrlValue(): any {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (this.ngControl.control as FormControl)?.value;
  }

  set modelCtrlValue(value: any) {
    if (this.ngControl.control) this.ngControl.control.setValue(value);
  }

  get selectedOptionDisplayValue(): string | null {
    const selectedDisplayValue: string[] = [];

    this.filteredOptions.forEach((option) => {
      if (Array.isArray(this.modelCtrlValue)) {
        if (this.modelCtrlValue.includes(option.key) && option.displayValue)
          selectedDisplayValue.push(option.displayValue);
        if (option.children) {
          option.children.forEach((child) => {
            if (this.modelCtrlValue.includes(child.key) && child.displayValue)
              selectedDisplayValue.push(child.displayValue);
          });
        }
      } else {
        if (option.key === this.modelCtrlValue && option.displayValue) selectedDisplayValue.push(option.displayValue);
        if (option.children) {
          option.children.forEach((child) => {
            if (child.key === this.modelCtrlValue && child.displayValue) selectedDisplayValue.push(child.displayValue);
          });
        }
      }
    });

    return selectedDisplayValue.join(', ');
  }

  /**
   * Custom error state matcher that checks the parent ngControl's state.
   * This ensures the error state respects form reset (which sets dirty to false).
   */
  get errorStateMatcher(): ErrorStateMatcher {
    return {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      isErrorState: (_control: FormControl | null, _form: FormGroupDirective | NgForm | null): boolean => {
        const ctrl = this.ngControl?.control;
        return !!(ctrl && ctrl.invalid && ctrl.touched && ctrl.dirty);
      },
    };
  }
}
